import { type JSX, useCallback, useEffect, useState } from "react";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import Drawer from "@via/components/drawer";
import { GenericError } from "@via/components/error.tsx";
import { LogoSpinner } from "@via/components/loading/loading.tsx";
import Table, { DrawerSize } from "@via/components/table/table.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import {
  useAppsList,
  useDeleteApp,
  useLaunchApplication,
  useRedeployApp,
} from "@via/pages/apps/apps.api.tsx";
import {
  AppDeployBadge,
  AppRowActions,
} from "@via/pages/apps/app-row-actions.tsx";
import {
  AppsActions,
  EMPTY_TABLE_MESSAGE,
  ERROR,
  ROUTES,
  USER_ROLES,
} from "@via/utils/constants.tsx";
import { classNames, getElapsedTime } from "@via/utils/helpers.tsx";
import {
  AppListResponseBodyItem,
  AppStatus,
  AppViewType,
  ContainerStatus,
  StartAppType,
} from "common";
import "./apps.css";
import EditAppForm from "@via/pages/apps/edit-app-form.tsx";
import {
  AgentEnvironmentNotice,
  RunOnClusterAction,
} from "@via/pages/apps/hpc/launch-dialog.tsx";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@via/hooks/useAuth";
import AppsFilter from "@via/pages/apps/apps-filter.tsx";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const defaultParams = {
  sort: [{ id: "dateCreated", desc: true }],
  filters: [{ id: "type", value: AppViewType.SharedWithMe }],
  page: 0,
  pageSize: 10,
};

const MyApps = (): JSX.Element => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.userRole === USER_ROLES.ADMIN;
  const canModifyApp = user?.featureFlags?.FF_MODIFY_APPS;
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [sort, setSort] = useState<Array<{ id: string; desc: boolean }>>(
    initialParams.sort
  );
  const [columnFilters, setColumnFilters] = useState<
    Array<{ id: string; value: unknown }>
  >(initialParams.filters);
  const [openEditDrawer, setEditDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<AppListResponseBodyItem>();
  const [tableData, setTableData] = useState<AppListResponseBodyItem[]>([]);
  const [appToBeDeleted, setAppToBeDeleted] =
    useState<AppListResponseBodyItem | null>(null);

  const {
    data: appsList,
    isError: isAppsListError,
    isLoading: isAppsListLoading,
    refetch,
  } = useAppsList(page, pageSize, sort, columnFilters);

  const {
    mutate: launchAppInstance,
    data: launchAppInstanceData,
    isPending: isLaunchAppInstanceLoading,
    isSuccess: isLaunchAppInstanceSuccess,
  } = useLaunchApplication();

  const { mutateAsync: deleteApp, isPending: isDeleteAppLoading } =
    useDeleteApp();

  const {
    mutate: redeployApp,
    isPending: isRedeployPending,
    variables: redeployingId,
  } = useRedeployApp();

  useEffect(() => {
    if (appsList?.data) {
      setTableData(appsList.data);
    }
  }, [appsList]);

  const handleVersionChange = newVersion => {
    // Find the selected row in the main tableData or within revisions
    let updatedRow = tableData.find(app => app.id === newVersion.value);
    let originalAppId = updatedRow?.id;

    if (!updatedRow) {
      // If not found in tableData, search within the revisions of each app
      tableData.forEach(app => {
        const revision = app.revisions?.find(
          rev => rev.id === newVersion.value
        );
        if (revision) {
          originalAppId = app.id;
          updatedRow = { ...app, ...revision }; // Merge the revision data into the main app object
        }
      });
    }

    if (updatedRow && originalAppId) {
      setSelectedRow(updatedRow);

      // Update the table data with the modified row
      const updatedTableData = tableData.map(app =>
        app.id === originalAppId ? updatedRow! : app
      );
      setTableData(updatedTableData);
    }
  };

  const handleContextMenuAction = (
    name: string,
    app: AppListResponseBodyItem
  ): void => {
    switch (name) {
      case AppsActions.Launch:
        // The button is disabled too; the backend refuses it with 409 either way.
        if (app.status === ContainerStatus.NotDeployed) {
          break;
        }
        launchAppInstance({
          id: app.id,
          payload: { type: StartAppType.Standalone },
        });
        break;
      case AppsActions.Redeploy:
        redeployApp(app.id);
        break;
      case AppsActions.Edit:
        handleEditAction(app);
        break;
      case AppsActions.Remove:
        setAppToBeDeleted(app);
        break;
    }
  };

  const handleEditAction = (app: AppListResponseBodyItem) => {
    setSelectedRow(app);
    setEditDrawerOpen(true);
  };

  useEffect(() => {
    setUrlParams({ sort, filters: columnFilters, page, pageSize });
  }, [sort, columnFilters, page, pageSize]);

  useEffect(() => {
    const isStatusCreating = tableData.some(
      app => app.status === ContainerStatus.Creating
    );
    if (isStatusCreating) {
      const interval = setInterval(() => {
        refetch();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [tableData, refetch]);

  useEffect(() => {
    if (isLaunchAppInstanceSuccess && launchAppInstanceData) {
      const { appStatus, accessUrl } = launchAppInstanceData;
      if (appStatus === AppStatus.Running) {
        window.open(accessUrl, "_blank", "noreferrer");
      }
      navigate({
        pathname: "/apps/manage-status",
      });
    }
  }, [isLaunchAppInstanceSuccess, launchAppInstanceData]);

  const getAppsListContext = useCallback(() => {
    return {
      apps: tableData.map(app => ({
        id: app.id,
        name: app.name,
        version: app.version,
        description: app.description,
        status: app.status,
        owner: app.owner?.username,
        dateCreated: app.dateCreated,
        dateModified: app.dateModified,
        own: app.own,
      })),
      total: appsList?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: columnFilters,
      sortBy: sort.length > 0 ? sort[0] : null,
    };
  }, [tableData, appsList?.total, page, pageSize, columnFilters, sort]);

  useRegisterCortexContext({
    routePattern: ROUTES.AVAILABLE_APPS,
    contextName: "My Apps",
    getContext: getAppsListContext,
    systemPrompt:
      "You are analyzing apps list page. Help answer questions about the apps list page.",
  });

  const columnHelper = createColumnHelper<AppListResponseBodyItem>();
  const columns = [
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        const isOwner = row.original.own;
        return (
          <div className="flex flex-row items-center">
            {isAdmin || (isOwner && canModifyApp) ? (
              <button
                className={classNames(
                  row.original.status === ContainerStatus.Creating
                    ? "disabled-link font-semibold"
                    : "via-link",
                  "truncate"
                )}
                onClick={e => {
                  e.preventDefault();
                  if (row.original.status !== ContainerStatus.Creating) {
                    handleContextMenuAction(AppsActions.Edit, row.original);
                  }
                }}>
                {row.original.name}
              </button>
            ) : (
              <span className="truncate">{row.original.name}</span>
            )}
            <AppDeployBadge status={row.original.status} />
          </div>
        );
      },
      header: () => <span>Name</span>,
      size: 100,
    }),
    columnHelper.accessor("version", {
      cell: info => {
        const currentVersion = info.row.original.version;
        const currentId = info.row.original.id;
        return (
          <div className="w-24">
            <SelectComponent
              name="credential-type"
              options={[
                ...(info.row.original.revisions
                  ? info.row.original.revisions.map(({ id, version }) => ({
                      label: version,
                      value: id,
                    }))
                  : [{ label: currentVersion, value: currentId }]),
              ]}
              onChange={handleVersionChange}
              value={{ label: currentVersion, value: currentId }}
              enableClear={false}
            />
          </div>
        );
      },
      header: () => <span>Version</span>,
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor("description", {
      cell: info => info.getValue(),
      header: () => <span>Description</span>,
      size: 180,

      enableSorting: false,
    }),
    columnHelper.accessor("dateModified", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value)}</span>;
      },
      header: () => <span>Last Modified</span>,
    }),
    columnHelper.accessor("dateCreated", {
      cell: info => {
        const value = info.getValue();
        return <span>{getElapsedTime(value)}</span>;
      },
      header: () => <span>Date Created</span>,
    }),
    columnHelper.accessor("name", {
      cell: ({ row }) => {
        return <span>{row.original.owner?.username}</span>;
      },
      header: () => "Owner",
      enableSorting: false,
    }),
    columnHelper.accessor("id", {
      cell: ({ row }) => (
        <div className="flex flex-wrap items-center gap-2">
          <AppRowActions
            app={row.original}
            isAdmin={isAdmin}
            canModifyApp={Boolean(canModifyApp)}
            redeploying={isRedeployPending && redeployingId === row.original.id}
            onAction={handleContextMenuAction}
          />
          {/* Nothing unless HPC apps are on and the app is a Shiny or Jupyter image (agent-9-1). */}
          <RunOnClusterAction app={row.original} />
        </div>
      ),
      header: () => "Options",
      size: 220,
      enableSorting: false,
    }),
  ] as Array<ColumnDef<AppListResponseBodyItem>>;
  return (
    <main className="py-4">
      {isAdmin || canModifyApp ? (
        <DeleteModal
          item={appToBeDeleted}
          setItem={setAppToBeDeleted}
          deleteFunction={() => deleteApp(appToBeDeleted!.id.toString())}
          isLoading={isDeleteAppLoading}
          title="Delete App"
        />
      ) : null}

      {/* One notice for the page instead of one per row (agent-9-1). */}
      <AgentEnvironmentNotice apps={tableData} />

      {isLaunchAppInstanceLoading ? (
        <div className="via-loading-spinner">
          <LogoSpinner />
        </div>
      ) : isAppsListError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <Table
          isLoading={isAppsListLoading}
          data={tableData}
          columns={columns}
          sort={sort}
          setSort={setSort}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          total={appsList?.total}
          emptyMessage={EMPTY_TABLE_MESSAGE.APPS}
          applyColumnSize={true}
          additionalFilters={
            <AppsFilter
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              setPage={setPage}
              setPageSize={setPageSize}
            />
          }
        />
      )}
      {isAdmin || canModifyApp ? (
        <Drawer
          id="my-apps-edit"
          open={openEditDrawer}
          setOpen={setEditDrawerOpen}
          size={DrawerSize.Medium}>
          <EditAppForm setOpen={setEditDrawerOpen} app={selectedRow} />
        </Drawer>
      ) : null}
    </main>
  );
};

export default MyApps;
